import React, { useEffect, useRef, useState } from 'react';
import { GameState, GamePhase } from '../types';
import { getCardEffect } from '../services/effects';
import { getLogIcon, getCardDisplayNameForCard } from '../constants';
import { Language, t, parseLog } from '../data/translations';
import { useShellLayout } from '../hooks/useShellLayout';

interface BattleLogProps {
    gameState: GameState;
    localPlayerId: number;
    onDropOnActivationChain?: (e: React.DragEvent, targetId: string) => void;
    onMouseEnterApp?: (card: any, rotation: number, source: string) => void;
    onMouseLeaveApp?: () => void;
    language: Language;
}

const BattleLog: React.FC<BattleLogProps> = ({
    gameState,
    localPlayerId,
    onDropOnActivationChain,
    onMouseEnterApp,
    onMouseLeaveApp,
    language
}) => {
    const logContainerRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const { isMobileShell } = useShellLayout();

    // Auto-scroll to bottom of log when new logs arrive
    useEffect(() => {
        if (logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
    }, [gameState.log]);

    if (isMobileShell) return null;

    return (
        <div className="w-80 flex flex-col gap-2 pointer-events-none font-sans select-none">
            {/* Container with Glassmorphism */}
            <div
                className="relative overflow-hidden rounded-xl bg-slate-950/80 backdrop-blur-xl border border-white/10 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] transition-all duration-300 pointer-events-none"
            >
                {/* Subtle Gradient Background Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-transparent to-purple-500/5 pointer-events-none" />

                {/* Header - Toggleable */}
                <div
                    className="relative px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors border-b border-white/5 pointer-events-auto"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse shadow-[0_0_8px_rgba(74,222,128,0.5)]" />
                        <span className="font-bold text-slate-100 text-sm tracking-widest uppercase mb-0.5">
                            {t('ui.battleLog', language)}
                        </span>
                    </div>
                    <span className={`text-xs text-amber-400 drop-shadow-[0_0_3px_rgba(251,191,36,0.8)] transition-all duration-300 hover:text-amber-300 hover:scale-125 ${isExpanded ? 'rotate-180' : ''}`}>
                        ▼
                    </span>
                </div>

                {/* ACTIVE STACKS SECTION - Render ALL active chains in reverse order (newest on top) */}
                <div className="flex flex-col gap-2 border-b border-white/10 bg-gradient-to-b from-amber-500/5 to-transparent p-3 animate-[fadeIn_0.3s_ease-out] pointer-events-auto">
                    {gameState.activations
                        .map((activation, index) => ({ activation, index }))
                        .filter(({ activation }) => !activation.resolved)
                        .reverse()
                        .map(({ activation, index }) => (
                            <div key={index} className="relative mb-2 last:mb-0">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-amber-400 text-[10px] font-bold uppercase tracking-wider">
                                            {t('ui.activeChain', language)}
                                        </span>
                                        <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 text-[9px] font-mono border border-amber-500/20">
                                            #{index + 1}
                                        </span>
                                    </div>
                                </div>

                                {/* The Stack Cards */}
                                <div
                                    className={`flex flex-col gap-1 relative z-10 pl-1 p-2 rounded border-2 transition-all duration-200 ${!activation.accepted
                                        ? 'border-dashed border-amber-500/30 hover:border-amber-400/60 bg-amber-500/5'
                                        : 'border-transparent'
                                        }`}
                                    onDragOver={(e) => {
                                        if (onDropOnActivationChain && !activation.accepted) e.preventDefault();
                                    }}
                                    onDrop={(e) => {
                                        if (onDropOnActivationChain && !activation.accepted) {
                                            // Pass the ID of the attacking card (root of this chain) as reference
                                            onDropOnActivationChain(e, activation.attackingCard.card.id);
                                        }
                                    }}
                                >
                                    {/* 1. The Attacking Card */}
                                    <ActiveChainItem
                                        card={activation.attackingCard.card}
                                        rotation={activation.attackingCard.rotation}
                                        ownerId={activation.attackingCard.card.ownerId}
                                        gameState={gameState}
                                        onMouseEnterApp={onMouseEnterApp}
                                        onMouseLeaveApp={onMouseLeaveApp}
                                        language={language}
                                        isAttacker={true}
                                    />

                                    {/* 2. The Chain (Responses) */}
                                    {activation.counterChain.map((item, idx) => (
                                        <ActiveChainItem
                                            key={idx}
                                            card={item.card}
                                            rotation={item.rotation}
                                            ownerId={item.card.ownerId}
                                            gameState={gameState}
                                            onMouseEnterApp={onMouseEnterApp}
                                            onMouseLeaveApp={onMouseLeaveApp}
                                            language={language}
                                        />
                                    ))}

                                    {/* Drop Hint */}
                                    {!activation.accepted && (
                                        <div className="absolute inset-x-0 bottom-0 top-1/2 flex items-end justify-center pb-2 opacity-0 hover:opacity-100 pointer-events-none transition-opacity">
                                            <span className="text-[10px] text-amber-200/50 uppercase tracking-widest font-bold bg-black/40 px-2 rounded backdrop-blur-sm">
                                                {t('ui.dropCounter', language)}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Status Footer */}
                                <div className="mt-1 flex items-center justify-end gap-2 text-[9px]">
                                    {!activation.accepted ? (
                                        <>
                                            <span className="text-yellow-200/60 italic">{t('ui.waiting', language)}...</span>
                                            <div className="flex gap-0.5">
                                                <div className="w-1 h-1 rounded-full bg-yellow-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                                                <div className="w-1 h-1 rounded-full bg-yellow-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                                            </div>
                                        </>
                                    ) : (
                                        <span className="text-green-400 font-bold tracking-wide animate-pulse">
                                            {t('ui.resolving', language)}...
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}

                    {gameState.activations.every(a => a.resolved) && (
                        <div className="text-center py-2 text-slate-500 text-[10px] italic">
                            {t('ui.noActiveChains', language)}
                        </div>
                    )}
                </div>

                {/* LOG HISTORY SECTION - Only rendered when expanded to avoid ghost hitbox */}
                {isExpanded && (
                    <div
                        className="bg-black/20 pointer-events-auto"
                    >
                        <div
                            ref={logContainerRef}
                            className="battle-log-scroll-area max-h-[24rem] overflow-y-auto p-3 flex flex-col gap-1 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent hover:scrollbar-thumb-white/20"
                        >
                            {[...gameState.log]
                                .filter(log => !log.includes('[REWARD]') && !log.includes('DEBUG') && !log.includes('State divergence'))
                                .map((log, index) => (
                                    <div
                                        key={index}
                                        className="group flex items-start gap-3 p-2 rounded hover:bg-white/5 transition-colors animate-[fadeIn_0.5s_ease-out]"
                                    >
                                        <div className="mt-0.5 opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300">
                                            {getLogIcon(log)}
                                        </div>
                                        <p className="text-xs leading-relaxed text-slate-300 group-hover:text-slate-100 transition-colors font-medium">
                                            {parseLog(log, language)}
                                        </p>
                                    </div>
                                ))}
                            {gameState.log.length === 0 && (
                                <div className="text-center py-8 text-slate-600 italic text-xs">
                                    {t('ui.noEventsYet', language)}...
                                </div>
                            )}
                            <div className="h-2" /> {/* Spacer */}
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

// Helper Sub-component for active chain items
const ActiveChainItem: React.FC<{
    card: any;
    rotation: number;
    ownerId: number;
    gameState: GameState;
    isAttacker?: boolean;
    isLatest?: boolean;
    onMouseEnterApp?: (card: any, rotation: number, source: string) => void;
    onMouseLeaveApp?: () => void;
    language: Language;
}> = ({ card, rotation, ownerId, gameState, isAttacker, isLatest, onMouseEnterApp, onMouseLeaveApp, language }) => {
    const ownerName = gameState.players.find(p => p.id === ownerId)?.name || 'Player';
    const effect = getCardEffect({ card, rotation: rotation, lockedState: null });

    // Determine visuals based on card type
    let borderColor = 'border-slate-700/50';
    let bgColor = 'bg-slate-800/40';
    let textColor = 'text-slate-200';
    let GlowClass = '';

    if (isAttacker) {
        borderColor = 'border-red-500/30';
        bgColor = 'bg-red-500/10';
        textColor = 'text-red-100';
    } else {
        borderColor = 'border-indigo-500/30';
        bgColor = 'bg-indigo-500/10';
        textColor = 'text-indigo-100';
    }

    if (isLatest) {
        borderColor = 'border-amber-500/50';
        bgColor = 'bg-amber-500/10';
        GlowClass = 'shadow-[0_0_15px_-3px_rgba(245,158,11,0.2)]';
    }

    return (
        <div className="relative pl-3">
            {/* Connector Line */}
            {!isAttacker && (
                <div className="absolute top-[-10px] left-[5px] w-[2px] h-[calc(100%+10px)] bg-slate-700/30 -z-10 rounded-full" />
            )}
            {!isAttacker && (
                <div className="absolute top-[12px] left-[5px] w-[8px] h-[2px] bg-slate-700/30 -z-10" />
            )}

            <div
                className={`
                    relative p-2 rounded-md border backdrop-blur-sm transition-all duration-300
                    ${borderColor} ${bgColor} ${GlowClass}
                    flex flex-col gap-0.5
                    group hover:border-white/20
                `}
                onMouseEnter={() => onMouseEnterApp?.(card, rotation, 'battle-log')}
                onMouseLeave={() => onMouseLeaveApp?.()}
            >
                <div className="flex items-center justify-between">
                    <span className={`font-bold text-[11px] uppercase tracking-wide truncate pr-2 ${textColor} flex items-center gap-2`}>
                        {getCardDisplayNameForCard(card, rotation, language)}
                    </span>
                    {/* Small indicator of who played it */}
                    <span className="text-[9px] text-slate-500 font-mono group-hover:text-slate-400 transition-colors">
                        {ownerName.substring(0, 8).toUpperCase()}
                    </span>
                </div >
            </div >
        </div >
    );
};

export default BattleLog;
